import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { createAction } from 'redux-act';
import { url } from 'utils/url';
import { log, errorMsg } from 'utils';

export const FOLDERS_FETCH_INIT = createAction('FOLDERS_FETCH_INIT');
export const FOLDERS_FETCH_SUCCESS = createAction('FOLDERS_FETCH_SUCCESS');
export const FOLDERS_FETCH_FAIL = createAction('FOLDERS_FETCH_FAIL');

export const FOLDERS_CREATE_INIT = createAction('FOLDERS_CREATE_INIT');
export const FOLDERS_CREATE_SUCCESS = createAction('FOLDERS_CREATE_SUCCESS');
export const FOLDERS_CREATE_FAIL = createAction('FOLDERS_CREATE_FAIL');

export const FOLDERS_UPDATE_INIT = createAction('FOLDERS_UPDATE_INIT');
export const FOLDERS_UPDATE_SUCCESS = createAction('FOLDERS_UPDATE_SUCCESS');
export const FOLDERS_UPDATE_FAIL = createAction('FOLDERS_UPDATE_FAIL');

export const FOLDERS_DELETE_INIT = createAction('FOLDERS_DELETE_INIT');
export const FOLDERS_DELETE_SUCCESS = createAction('FOLDERS_DELETE_SUCCESS');
export const FOLDERS_DELETE_FAIL = createAction('FOLDERS_DELETE_FAIL');

export const FOLDERS_BULK_COPY_INIT = createAction('FOLDERS_BULK_COPY_INIT');
export const FOLDERS_BULK_COPY_SUCCESS = createAction(
  'FOLDERS_BULK_COPY_SUCCESS'
);
export const FOLDERS_BULK_COPY_FAIL = createAction('FOLDERS_BULK_COPY_FAIL');

export const FOLDERS_BULK_DELETE_INIT = createAction(
  'FOLDERS_BULK_DELETE_INIT'
);
export const FOLDERS_BULK_DELETE_SUCCESS = createAction(
  'FOLDERS_BULK_DELETE_SUCCESS'
);
export const FOLDERS_BULK_DELETE_FAIL = createAction(
  'FOLDERS_BULK_DELETE_FAIL'
);

export const FOLDERS_MOVE_INIT = createAction('FOLDERS_MOVE_INIT');
export const FOLDERS_MOVE_SUCCESS = createAction('FOLDERS_MOVE_SUCCESS');
export const FOLDERS_MOVE_FAIL = createAction('FOLDERS_MOVE_FAIL');

export const FOLDERS_CLEAR_DATA = createAction('FOLDERS_CLEAR_DATA');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

export const fetchFolders = ({ siteId, parentId, search }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_FETCH_INIT());

    try {
      const params = { siteId };

      // Omitting parentId returns every folder in the site, which is what the
      // sidebar tree needs; passing it scopes the fetch to one level.
      if (parentId !== undefined) params.parentId = parentId;
      if (search) params.search = search;

      const res = await axios.get(`${url}/folders`, {
        params,
        headers: authHeaders(),
      });

      log('Fetched folders:', res.data);
      return dispatch(FOLDERS_FETCH_SUCCESS({ folders: res.data }));
    } catch (error) {
      toastr.error('', `${errorMsg(dispatch, error)}`);
      return dispatch(FOLDERS_FETCH_FAIL({ error }));
    }
  };
};

export const createFolder = ({ name, parentId, siteId }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_CREATE_INIT());

    try {
      const res = await axios.post(
        `${url}/folders/create`,
        { name, parentId: parentId || null, siteId },
        { headers: authHeaders() }
      );

      toastr.success('', 'Folder created successfully');
      dispatch(fetchFolders({ siteId }));

      return dispatch(FOLDERS_CREATE_SUCCESS({ folder: res.data }));
    } catch (error) {
      log(error);
      const errorMessage =
        error?.response?.data?.message || 'Failed to create folder';
      toastr.error('', errorMessage);
      return dispatch(FOLDERS_CREATE_FAIL({ error: errorMessage }));
    }
  };
};

export const updateFolder = ({ id, name, parentId, siteId }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_UPDATE_INIT());

    try {
      const res = await axios.put(
        `${url}/folders/${id}`,
        { name, parentId: parentId || null },
        { headers: authHeaders() }
      );

      toastr.success('', 'Folder updated successfully');

      if (siteId) dispatch(fetchFolders({ siteId }));

      return dispatch(FOLDERS_UPDATE_SUCCESS({ folder: res.data }));
    } catch (error) {
      log(error);
      const errorMessage =
        error?.response?.data?.message || 'Failed to update folder';
      toastr.error('', errorMessage);
      return dispatch(FOLDERS_UPDATE_FAIL({ error: errorMessage }));
    }
  };
};

export const deleteFolder = ({ id, siteId }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_DELETE_INIT());

    try {
      await axios.delete(`${url}/folders/${id}`, { headers: authHeaders() });

      toastr.success('', 'Folder deleted successfully');
      dispatch(fetchFolders({ siteId }));

      return dispatch(FOLDERS_DELETE_SUCCESS({ id }));
    } catch (error) {
      log(error);
      const errorMessage =
        error?.response?.data?.message || 'Failed to delete folder';
      toastr.error('', errorMessage);
      return dispatch(FOLDERS_DELETE_FAIL({ error: errorMessage }));
    }
  };
};

export const bulkCopyFolders = ({ folderIds, siteId }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_BULK_COPY_INIT());

    try {
      const res = await axios.post(
        `${url}/folders/bulk-copy`,
        { folderIds },
        { headers: authHeaders() }
      );

      toastr.success('', 'Folders copied successfully');
      dispatch(fetchFolders({ siteId }));

      return dispatch(FOLDERS_BULK_COPY_SUCCESS({ folders: res.data.data }));
    } catch (error) {
      log(error);
      const errorMessage =
        error?.response?.data?.message || 'Failed to copy folders';
      toastr.error('', errorMessage);
      return dispatch(FOLDERS_BULK_COPY_FAIL({ error: errorMessage }));
    }
  };
};

export const bulkDeleteFolders = ({ folderIds, siteId }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_BULK_DELETE_INIT());

    try {
      const res = await axios.post(
        `${url}/folders/bulk-delete`,
        { folderIds },
        { headers: authHeaders() }
      );

      const count = res.data.deletedFolders ?? res.data.deletedCount ?? 0;
      toastr.success(
        '',
        `${count} folder${count > 1 ? 's' : ''} deleted successfully`
      );
      dispatch(fetchFolders({ siteId }));

      return dispatch(
        FOLDERS_BULK_DELETE_SUCCESS({
          deletedCount: count,
          deletedScreens: res.data.deletedScreens ?? 0,
        })
      );
    } catch (error) {
      log(error);
      const errorMessage =
        error?.response?.data?.message || 'Failed to delete folders';
      toastr.error('', errorMessage);
      return dispatch(FOLDERS_BULK_DELETE_FAIL({ error: errorMessage }));
    }
  };
};

export const moveFolder = ({ folderId, newParentId, siteId }) => {
  return async (dispatch) => {
    dispatch(FOLDERS_MOVE_INIT());

    try {
      const res = await axios.put(
        `${url}/folders/${folderId}/move`,
        { newParentId },
        { headers: authHeaders() }
      );

      toastr.success('', 'Folder moved successfully');
      dispatch(fetchFolders({ siteId }));

      return dispatch(FOLDERS_MOVE_SUCCESS({ folder: res.data }));
    } catch (error) {
      log(error);
      const errorMessage =
        error?.response?.data?.message || 'Failed to move folder';
      toastr.error('', errorMessage);
      return dispatch(FOLDERS_MOVE_FAIL({ error: errorMessage }));
    }
  };
};

export const clearFoldersData = () => (dispatch) =>
  dispatch(FOLDERS_CLEAR_DATA());
